const KEYS = {
  idToken: "n8nvibes_id_token",
  refreshToken: "n8nvibes_refresh_token",
} as const;

const FIREBASE_API_KEY = import.meta.env.VITE_FIREBASE_API_KEY;

export function storeTokens(idToken: string, refreshToken: string): void {
  sessionStorage.setItem(KEYS.idToken, idToken);
  sessionStorage.setItem(KEYS.refreshToken, refreshToken);
}

export function getStoredTokens(): { idToken: string; refreshToken: string } | null {
  const idToken = sessionStorage.getItem(KEYS.idToken);
  const refreshToken = sessionStorage.getItem(KEYS.refreshToken);
  if (!idToken || !refreshToken) return null;
  return { idToken, refreshToken };
}

export function clearStoredTokens(): void {
  sessionStorage.removeItem(KEYS.idToken);
  sessionStorage.removeItem(KEYS.refreshToken);
}

function decodeJwtPayload(token: string): Record<string, unknown> {
  const parts = token.split(".");
  if (parts.length !== 3) throw new Error("Invalid JWT");
  const payload = atob(parts[1].replace(/-/g, "+").replace(/_/g, "/"));
  return JSON.parse(payload);
}

function isTokenExpired(token: string): boolean {
  try {
    const payload = decodeJwtPayload(token);
    const exp = payload.exp as number;
    if (!exp) return true;
    return Date.now() / 1000 > exp - 60;
  } catch {
    return true;
  }
}

async function refreshIdToken(refreshToken: string): Promise<{ idToken: string; refreshToken: string }> {
  const url = `https://securetoken.googleapis.com/v1/token?key=${FIREBASE_API_KEY}`;
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: `grant_type=refresh_token&refresh_token=${encodeURIComponent(refreshToken)}`,
  });
  if (!res.ok) throw new Error(`Token refresh failed (${res.status})`);
  const data = (await res.json()) as { id_token: string; refresh_token: string };
  return { idToken: data.id_token, refreshToken: data.refresh_token };
}

export async function getValidToken(): Promise<string | null> {
  const stored = getStoredTokens();
  if (!stored) return null;

  if (!isTokenExpired(stored.idToken)) return stored.idToken;

  try {
    const refreshed = await refreshIdToken(stored.refreshToken);
    storeTokens(refreshed.idToken, refreshed.refreshToken);
    return refreshed.idToken;
  } catch {
    clearStoredTokens();
    return null;
  }
}

export function getTokenUser(): { email: string; uid: string; displayName: string | null } | null {
  const stored = getStoredTokens();
  if (!stored) return null;
  try {
    const payload = decodeJwtPayload(stored.idToken);
    return {
      email: (payload.email as string) || "",
      uid: (payload.sub as string) || (payload.user_id as string) || "",
      displayName: (payload.name as string) || null,
    };
  } catch {
    return null;
  }
}
